import { RadioGroupInput, Typography, handleApiRequest } from "@cpj-ai/panel-npm-package";
import {
  AuthAccountOrganizationJobPositionStatusChoices,
  UpdateJobPositionStatusMutation,
  UpdateJobPositionStatusMutationVariables,
  useUpdateJobPositionStatusMutation,
} from "@graphql/generated";
import { Button, Modal, ModalBody, ModalContent, ModalProps } from "@heroui/react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";

export type ChangeStatusModalProps = Pick<ModalProps, "isOpen" | "onClose" | "onOpenChange"> & {
  refreshData?: () => void;
  status?: AuthAccountOrganizationJobPositionStatusChoices;
};

export const ChangeStatusModal: React.FC<ChangeStatusModalProps> = ({
  isOpen,
  onClose,
  onOpenChange,
  refreshData,
  status,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<AuthAccountOrganizationJobPositionStatusChoices | undefined>(
    status,
  );

  const { id } = useParams();
  const [updateJobPositionStatus, { loading }] = useUpdateJobPositionStatusMutation();

  const handleSelectStatus = async () => {
    if (!id || !selectedStatus) {
      toast.error("Please select a status");
      return;
    }

    await handleApiRequest<UpdateJobPositionStatusMutation, UpdateJobPositionStatusMutationVariables>({
      apiFunction: updateJobPositionStatus,
      variables: {
        id,
        status: selectedStatus,
      },
      componentName: "ChangeStatusModal",
      actionName: "updateJobPositionStatus",
      onSuccess: () => {
        refreshData?.();
        onClose?.();
      },
      successMessage: "Job post status updated successfully",
      fallbackErrorMessage: "Failed to update job post status",
      extraErrorContext: {
        jobPositionId: id,
        newStatus: selectedStatus,
      },
    });
  };

  useEffect(() => {
    setSelectedStatus(status);
  }, [status]);

  return (
    <Modal
      isOpen={isOpen ?? false}
      onClose={onClose ?? (() => {})}
      onOpenChange={onOpenChange ?? (() => {})}
      size="2xl"
    >
      <ModalContent className="p-4">
        <ModalBody className="flex items-center gap-8">
          <RadioGroupInput
            value={selectedStatus ?? null}
            onChange={(e) => {
              setSelectedStatus(e?.target?.value as AuthAccountOrganizationJobPositionStatusChoices);
            }}
            classNames={{
              label: "text-lg text-black",
            }}
            orientation="vertical"
            label={"Which status do you want to change the job status to?"}
            items={[
              {
                label: (
                  <CustomLabel
                    title="Suspended"
                    description="The recruitment process is temporarily suspended and closed  for some reason."
                  />
                ),
                value: AuthAccountOrganizationJobPositionStatusChoices.SUSPENDED,
              },
            ]}
          />
          <Button
            color="success"
            className="w-60"
            onClick={handleSelectStatus}
            isLoading={loading}
            isDisabled={loading}
          >
            Save
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const CustomLabel = ({ title, description }: { title: string; description: string }) => {
  return (
    <div>
      <Typography className="text-black">{title}</Typography>
      <Typography variant="muted">{description}</Typography>
    </div>
  );
};
